/* No libraries, network requests, or permanent animation loop. */
(() => {
  if (customElements.get('az-pet-categories')) return;

  class AZPetCategories extends HTMLElement {
    connectedCallback() {
      if (this.lifecycle) return;
      this.lifecycle = new AbortController();
      this.reduced = matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = matchMedia('(hover: hover) and (pointer: fine)');
      this.cards = [...this.querySelectorAll('.az-pets__link')];
      this.animations = new Set();
      this.revealedTargets ||= new WeakSet();
      const options = { signal: this.lifecycle.signal };
      this.reduced.addEventListener('change', () => this.configureMotion(), options);
      this.finePointer.addEventListener('change', () => this.configureMotion(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.resetMotion();
      }, options);
      const editorTarget = this.closest('.shopify-section') || this;
      editorTarget.addEventListener('shopify:block:select', () => this.finishReveal(), options);
      editorTarget.addEventListener('shopify:section:select', () => this.finishReveal(), options);
      this.configureMotion();
    }

    configureMotion() {
      this.motion?.abort();
      this.observer?.disconnect();
      this.revealObserver?.disconnect();
      this.resetMotion();
      this.finishReveal();
      this.motion = new AbortController();
      const options = { signal: this.motion.signal, passive: true };
      const canAnimate = !this.reduced.matches;
      const canParallax = canAnimate && this.finePointer.matches && this.dataset.parallax === 'true';

      if (canParallax) {
        this.cards.forEach(card => {
          card.addEventListener('pointermove', event => {
            if (event.pointerType === 'touch') return;
            if (this.activeCard && this.activeCard !== card) this.resetMotion();
            const rect = card.getBoundingClientRect();
            const strength = Number(this.dataset.parallaxStrength) || 0;
            this.activeCard = card;
            this.targetX = Math.max(-1, Math.min(1, (event.clientX - rect.left) / rect.width * 2 - 1)) * strength;
            this.targetY = Math.max(-1, Math.min(1, (event.clientY - rect.top) / rect.height * 2 - 1)) * strength;
            if (!this.frame) this.frame = requestAnimationFrame(() => this.tick());
          }, options);
          card.addEventListener('pointerleave', () => {
            this.targetX = this.targetY = 0;
            if (this.activeCard && !this.frame) this.frame = requestAnimationFrame(() => this.tick());
          }, options);
          card.addEventListener('pointercancel', () => this.resetMotion(), options);
        });
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(entries => {
          entries.forEach(entry => {
            if (!entry.isIntersecting) this.resetMotion();
          });
        }, { threshold: 0.08 });
        this.observer.observe(this);
        if (canAnimate && this.dataset.reveal === 'true' && !window.Shopify?.designMode) {
          this.revealObserver = new IntersectionObserver(entries => {
            let order = 0;
            entries.forEach(entry => {
              if (!entry.isIntersecting) return;
              this.revealObserver.unobserve(entry.target);
              if (this.revealedTargets.has(entry.target)) return;
              this.revealedTargets.add(entry.target);
              this.reveal(entry.target, order++);
            });
          }, { threshold: .12 });
          this.querySelectorAll('.az-pets__card, .az-pets__benefit').forEach(target => this.revealObserver.observe(target));
        }
      }
    }

    tick() {
      this.frame = 0;
      if (!this.activeCard || !this.isConnected) return;
      this.x = (this.x || 0) + (this.targetX - (this.x || 0)) * .14;
      this.y = (this.y || 0) + (this.targetY - (this.y || 0)) * .14;
      this.activeCard.style.setProperty('--az-x', `${this.x.toFixed(2)}px`);
      this.activeCard.style.setProperty('--az-y', `${this.y.toFixed(2)}px`);
      this.activeCard.style.setProperty('--az-glow-x', `${(this.x * -.45).toFixed(2)}px`);
      this.activeCard.style.setProperty('--az-glow-y', `${(this.y * -.45).toFixed(2)}px`);
      if (Math.abs(this.targetX - this.x) > .03 || Math.abs(this.targetY - this.y) > .03) {
        this.frame = requestAnimationFrame(() => this.tick());
      }
    }

    resetMotion() {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.x = this.y = this.targetX = this.targetY = 0;
      this.activeCard = null;
      this.cards?.forEach(card => ['--az-x', '--az-y', '--az-glow-x', '--az-glow-y'].forEach(property => card.style.removeProperty(property)));
    }

    reveal(target, index) {
      if (!target.animate) return;
      const animation = target.animate([
        { opacity: 0, transform: 'translateY(14px)' },
        { opacity: 1, transform: 'translateY(0)' }
      ], {
        duration: Number(this.dataset.revealDuration) || 500,
        delay: index * (Number(this.dataset.revealStagger) || 0),
        easing: 'cubic-bezier(.2,.65,.3,1)', fill: 'backwards'
      });
      this.animations.add(animation);
      animation.onfinish = animation.oncancel = () => this.animations.delete(animation);
    }

    finishReveal() {
      this.animations?.forEach(animation => animation.cancel());
      this.animations?.clear();
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.motion?.abort();
      this.observer?.disconnect();
      this.revealObserver?.disconnect();
      this.resetMotion();
      this.finishReveal();
      this.lifecycle = null;
    }
  }

  customElements.define('az-pet-categories', AZPetCategories);
})();
